import { useState } from 'react'
import StudioMark from './components/StudioMark'
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { withFilter } from './useCalendarFilter'
import CalendarWeekView from './components/CalendarWeekView'
import { type CalendarView } from './components/CalendarViewSwitcher'
import CalendarMonthView from './components/CalendarMonthView'
import CalendarDayView from './components/CalendarDayView'
import TeacherDayView from './components/TeacherDayView'
import { MyClasses } from './components/TeacherPortalView'
import StudioNewsPanel from './components/StudioNewsPanel'
import AttendanceScreen from './components/AttendanceScreen'
import BankedClassesView from './components/BankedClassesView'
import ScheduleScreen from './components/ScheduleScreen'
import NavRail from './components/NavRail'
import ViewBar from './components/ViewBar'
import NeedsADecision from './components/NeedsADecision'
import NotificationBell from './components/NotificationBell'
import NotificationLink from './components/NotificationLink'
import HomeScreen from './components/HomeScreen'
import { landingFor } from './landing'
import { navSubjectFrom } from './navSubject'
import { deviceToken } from './api/attendance'
import SearchBox from './components/SearchBox'
import WhereIsView from './components/WhereIsView'
import ConflictsView from './components/ConflictsView'
import ImportView from './components/ImportView'
import FeedActivityView from './components/FeedActivityView'
import CarryoverView from './components/CarryoverView'
import CatalogView from './components/CatalogView'
import ClassesView from './components/ClassesView'
import { ClassRecordRoute } from './components/ClassRecordView'
import MeasuringView from './components/MeasuringView'
import ClassroomView from './components/ClassroomView'
import StaffAdminView from './components/StaffAdminView'
import SignInView from './components/SignInView'
import AcceptInviteView from './components/AcceptInviteView'
import RequireSession from './components/RequireSession'
import MessagesLayout, { NoConversationPicked } from './components/MessagesLayout'
import ConversationView from './components/ConversationView'
import ReviewConversationsView from './components/ReviewConversationsView'
import SupervisoryTranscript from './components/SupervisoryTranscript'
import NewConversationView from './components/NewConversationView'
import RequireCapability from './components/RequireCapability'
import RequireDestination from './components/RequireDestination'
import AsksView from './components/AsksView'
import DestinationScreen from './components/DestinationScreen'
import AdminCoverView from './components/AdminCoverView'
import NavMenu from './components/NavMenu'
import PublicShell from './components/PublicShell'
import HomeView from './components/HomeView'
import AboutView from './components/AboutView'
import ContentEditorView from './components/ContentEditorView'
import MediaLibraryView from './components/MediaLibraryView'
import DeviceAdminView from './components/DeviceAdminView'
import PeopleAdminView from './components/PeopleAdminView'
import DancerAdminView from './components/DancerAdminView'
import ClaimAccountView from './components/ClaimAccountView'
import StudioSetupView from './components/StudioSetupView'
import NotificationTemplatesView from './components/NotificationTemplatesView'
import ForgotPasswordView from './components/ForgotPasswordView'
import ResetPasswordView from './components/ResetPasswordView'
import DancerRecordView from './components/DancerRecordView'
import AccountView from './components/AccountView'
import MissingAttendanceView from './components/MissingAttendanceView'
import WhatsNewDialog from './components/WhatsNewDialog'
import WhatsNewView from './components/WhatsNewView'
import StandingView from './components/StandingView'
import ReportedAbsencesView from './components/ReportedAbsencesView'
import ThemeToggle from './components/ThemeToggle'
import BuildCredit from './components/BuildCredit'
import FeedbackButton from './components/FeedbackButton'
import DashboardView from './components/DashboardView'
import FeedbackView from './components/FeedbackView'
import SimulatedDateBanner from './components/SimulatedDateBanner'
import NewVersionBanner from './components/NewVersionBanner'
import NoticeBanner from './components/NoticeBanner'
import NoticesView from './components/NoticesView'
import WelcomeDialog from './components/WelcomeDialog'
import { useSession, useSignOut } from './useSession'
import type { Session } from './api/auth'
import { useQuery } from '@tanstack/react-query'
import { today } from './api/calendar'
import { fetchContent, text as contentText } from './api/content'
import { initials } from './api/people'

/**
 * The front door.
 *
 * Marketing homepage for a visitor; the calendar for anyone signed in. A
 * teacher who types the domain wants tonight's schedule, not the studio's sales
 * pitch — and a signed-in parent has no use for "Register a dancer" as a hero
 * button.
 */
/**
 * The front door, which asks what this *device* is before asking who you are
 * (D-116, D-925).
 *
 * **A claimed tablet finds its way home.** A wall tablet holds a device token
 * and no session, so `/` sent it to the marketing page — after a reboot, after
 * an idle redirect, after anybody tapped the address bar — and somebody had to
 * walk over and type the address back in, on the one screen in the studio read
 * standing up between two classes.
 *
 * ⚠ **The session is asked first, and that order is the whole safety of it.**
 * A teacher's own phone is a classroom device (D-128), so her phone may hold a
 * token as well; deciding on the token first would send her to a register
 * every time she opened the app and leave her no way to her own account.
 * **And only this route** — `/signin` is untouched, so the way back in is
 * always there.
 *
 * ⚠ **It is a separate function from `HomeRoute` on purpose**, and a check
 * made it one. `HomeRoute` owns the signed-in *ladder* and `landing.test.ts`
 * forbids it naming any address of its own, because that is how D-331 came to
 * be applied in one place and not the other (D-370). *Where does this person
 * land* and *what is this device* are two questions; putting the second in
 * the function that answers the first set the guard off, correctly.
 */
function FrontDoor() {
  const { session, isLoading } = useSession()

  if (isLoading) return null
  if (!session && deviceToken()) return <Navigate to="/room" replace />

  return <HomeRoute />
}

function HomeRoute() {
  const { session, isLoading } = useSession()

  // Nothing while the session is in flight, or a signed-in teacher sees the
  // marketing page flash before being moved (same reasoning as RequireSession).
  if (isLoading) return null

  /*
   * Signed in goes somewhere useful, and "useful" differs by who you are —
   * front doors onto one system (D-128).
   *
   * **The ladder lives in `landingFor` now** (D-370). It used to be written out
   * here, and `SignedInHome` carried a second answer to the same question —
   * D-331's composed home. This route is in the outer tree, so it matched `/`
   * first and that composed home was **unreachable**: a teacher who is also a
   * parent landed on `/teacher` with her children nowhere on the screen, which
   * is what the owner reported. Two screens answering one question is the shape
   * that caused it, so there is one function and it has its own tests.
   */
  if (session) {
    return <Navigate to={landingFor(navSubjectFrom(session))} replace />
  }

  return (
    <PublicShell>
      <HomeView />
    </PublicShell>
  )
}

/**
 * Where a view change goes from the studio calendar (D-395).
 *
 * **These three screens are the only ones allowed to route this way**, and
 * that is the point: every `/calendar/*` address is behind
 * `RequireCapability("ViewAllSchedules")`, so navigating between them is safe
 * from here and nowhere else. It used to live inside `CalendarViewSwitcher` as
 * the fallback when a caller left `onSelect` off, which meant an embed on a
 * parent screen inherited a navigation it was never permitted to make — from a
 * prop nobody had written (D-312 is that bug, once already).
 *
 * The month is the odd one: its own address carries no date, and switching to
 * Week on the 1st of October would land on a week that is mostly September, so
 * the view hands on the 15th and this takes it as given.
 */
function viewHref(view: CalendarView, date: string): string {
  return view === 'month' ? '/calendar/month' : `/calendar/${view}/${date}`
}

/*
 * The route owns the week, now that the view no longer does (D-360).
 *
 * **`replace`, so paging four weeks forward leaves one entry in the back stack
 * rather than four** — the same call `ParentSchedulePanel` makes for the same
 * reason. Back should leave the calendar, not undo one tap of an arrow.
 *
 * It also makes the master calendar's week linkable, which it was not: the
 * address said `/calendar/week/<the day you arrived>` however far you had
 * paged, because the anchor lived inside the component.
 *
 * A view change is *not* a replace: switching Week to Day is a place you can
 * come back from, unlike paging within one.
 */
function WeekRoute() {
  const { date } = useParams<{ date: string }>()
  const navigate = useNavigate()
  const [params] = useSearchParams()

  return (
    <CalendarWeekView
      date={date ?? today()}
      onDate={(next) => navigate(withFilter(`/calendar/week/${next}`, params), { replace: true })}
      onView={(view, on) => navigate(withFilter(viewHref(view, on), params))}

    />
  )
}

function DayRoute() {
  const { date } = useParams<{ date: string }>()
  const navigate = useNavigate()
  const [params] = useSearchParams()

  return (
    <CalendarDayView
      date={date ?? today()}
      onDate={(next) => navigate(withFilter(`/calendar/day/${next}`, params), { replace: true })}
      onView={(view, on) => navigate(withFilter(viewHref(view, on), params))}
    />
  )
}

/*
 * The month has no route parameter, so the state lives here rather than in the
 * view — which keeps all three calendar views on one contract: the caller says
 * where the anchor lives, and there is no second, uncontrolled mode to reason
 * about (D-360).
 */
function MonthRoute() {
  const [anchor, setAnchor] = useState(today())
  const navigate = useNavigate()
  const [params] = useSearchParams()

  return (
    <CalendarMonthView
      date={anchor}
      onDate={setAnchor}
      onView={(view, on) => navigate(withFilter(viewHref(view, on), params))}
    />
  )
}


/**
 * Site header.
 *
 * Deliberately holds only what is global. Day / week / month moved down beside
 * the date controls, because choosing a view is the same kind of decision as
 * choosing a week, and this leaves room for real navigation as the app grows.
 */
function Nav() {
  const { session } = useSession()

  /*
   * No capability reads left at all, which is the point.
   *
   * This component read eleven - ManageIntegrations, ManageStaff,
   * EditSiteContent, ManageMedia, TakeAttendance, ViewAttendance,
   * ManageClasses, ManagePeople, ManageNotificationTemplates, ApproveBookings,
   * ViewAllSchedules - and every one of them gated a *place*. Places moved to
   * the rail, where one derived list decides them all (D-322). The last was
   * ViewPeople, gating "Find a dancer": a question rather than a place, and now
   * a search box the server scopes per row (D-325).
   *
   * Eleven capability reads in a header is what a hand-authored navigation
   * costs, and none of them was wrong on its own.
   */
  return (
    <header className="border-b border-line bg-surface">
      {/*
        Positioned so the bell's panel and the search results hang from this whole row on
        a phone instead of running off the left edge (NotificationBell, SearchBox).
        Anything positioned between this row and them - a `relative` on the nav, say -
        quietly undoes that, and the phone-header-panels walk in audit-deep.mjs says so.
      */}
      <div className="relative mx-auto flex max-w-[1400px] flex-wrap items-center gap-4 px-5 py-3">
        {/*
          The brand is the way home, which it was not before (D-315).

          It rendered as a plain `div`, so the header had no "back to where I
          started" at all — and the teacher nav carried a "My classes" link to
          `/teacher` doing that job under a name that collides with a tab
          *inside* `/teacher` called the same thing. Two controls, one label,
          different destinations.

          `/` resolves per account (D-176): the studio calendar for Tonya, the
          teacher portal for a teacher, the family portal for a parent — so one
          link is correct for everyone without testing a role.
        */}
        <Link
          to="/"
          className="flex items-center gap-3 no-underline"
          aria-label="Go to your home screen"
        >
          <StudioMark size="sm" />
          <div>
            <p className="text-[11px] font-semibold tracking-[0.11em] text-ink-3 uppercase">
              Cabot Dance Academy
            </p>
            <p className="text-[13px] font-semibold text-ink">Studio System</p>
          </div>
        </Link>

        {/*
          Wraps, because on a phone it otherwise does not (D-229).

          The row containing it wraps and this one did not, so the bar was
          573px wide inside a 390px window and every signed-in screen scrolled
          sideways — the account menu and the theme toggle sat past the right
          edge, off screen, with no way to reach them. `justify-end` keeps it
          against the right on a wide window, where it still reads as one bar.
        */}
        {/*
          What is left of the header once the rail carries the destinations
          (D-322, D-323).

          Calendar, Studio, Attendance and the gear were all *places*, and
          places belong on the rail - that was the whole complaint: a top nav
          and a second nav inside the content area, neither of which was the
          navigation. What stays here is what is not a place: who you are, and
          how the app looks.

          <b>Find a dancer stays for now and is on notice.</b> It is a
          question rather than a place (D-325), so it becomes the search box in
          step 5. Removing it before that exists would take away the only way a
          teacher reaches a dancer.

          Wraps, because on a phone it otherwise does not: this row was 573px
          wide inside a 390px window and put the account menu off screen with
          no way to reach it (D-229).
        */}
        <nav className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {/*
            The question, where the link to a place used to be (D-325).

            "Find a dancer" was a header link to a screen whose only job was to
            hold a text box - a place invented to house a question - and it was
            gated on `ViewPeople`, so a parent could not ask it about her own
            child. This is scoped per row instead, so everybody can ask and
            everybody gets a different answer.
          */}
          <SearchBox />

          {/* What just happened, beside who you are (D-324). */}
          <NotificationBell />

          {/*
            Telling us what you found, from wherever you found it (D-607).
            In the header because a pilot tester will not go looking for it,
            and the value of asking inside the app is that the thought and the
            form are in the same place.
          */}
          <FeedbackButton />

          {session && <AccountMenu session={session} />}

          <ThemeToggle />
        </nav>
      </div>
    </header>
  )
}

/**
 * The account menu, and for sixteen people the way between their two halves.
 *
 * **The switch lives here rather than in the header bar** (D-309). It costs no
 * phone width, which is already the constraint — and the gear beside it could
 * never have held it, since every item in that menu is gated on an admin
 * capability and it disappears entirely for a teacher or a parent (D-176).
 *
 * Choosing a menu over a visible toggle costs two things, and both are paid
 * here rather than assumed: the closed button names the current view, and it
 * carries a mark until she has opened it once.
 */
function AccountMenu({ session }: { session: Session }) {
  const navigate = useNavigate()
  const signOut = useSignOut()

  /*
   * The link is a ContentBlock, so Tonya can change or retire it without a
   * developer (A-16). Cached hard — it changes about once a year, and a header
   * that re-fetches it on every navigation would be silly.
   */
  const { data: content } = useQuery({
    queryKey: ['content', 'portal'],
    queryFn: fetchContent,
    staleTime: 600_000,
  })

  const jackrabbit = content ? (contentText(content, 'portal.jackrabbit.url') || null) : null


  /*
   * Which side she is looking at, from the URL rather than from what is
   * stored.
   *
   * A control you cannot see is fine; a **state** you cannot see is not, and
   * the two portals look similar enough at a glance to be mistaken for each
   * other. Reading the stored preference instead would let the label disagree
   * with the page whenever she followed a link from a notification.
   */
  return (
    <div>
      {/*
        The switch is gone (D-331, retiring D-309).

        It existed to choose between two portals, and there are not two any
        more: one navigation, and Home and Schedule both hold whichever halves
        apply. A control that switches between things that no longer differ is
        a control that lies, which is the fault this whole rework began from.

        The name loses its suffix with it - "Kim May · Teacher" was there
        because a state you cannot see is not fine, and there is no longer a
        state to see.
      */}
      <NavMenu
        label={session.displayName}
        icon={
          // Initials rather than a generic person icon: on a shared
          // office machine, whose session this is matters.
          <span className="grid size-full place-items-center rounded-lg bg-accent-soft text-[11px] font-bold text-accent-2">
            {initials(session.displayName)}
          </span>
        }
        groups={[
          {
            /*
             * Jackrabbit, reachable from anywhere rather than only from the
             * fifth tab of the portal (D-313).
             *
             * <b>A menu item rather than a card in the header</b>, because it
             * is temporary: Jackrabbit owns billing only until it does not
             * (D-111), and permanent screen real estate for something being
             * retired is the wrong trade. This costs no phone width.
             *
             * `onSelect` rather than `to`, since it leaves the app entirely and
             * a NavLink would try to route it. `noopener` matters: without it
             * the opened tab can navigate this one.
             */
            heading: 'Billing',
            items: [
              {
                label: 'Jackrabbit parent portal',
                description: 'Invoices, card details, balance',
                visible: jackrabbit !== null,
                onSelect: () => window.open(jackrabbit!, '_blank', 'noopener,noreferrer'),
              },
            ],
          },
          {
            heading: session.displayName,
            items: [
              {
                label: 'Your account',
                description: 'Face ID, fingerprint and passkeys',
                to: '/account',
              },
              {
                label: 'Sign out',
                onSelect: () =>
                  signOut.mutate(undefined, {
                    onSuccess: () => navigate('/signin', { replace: true }),
                  }),
                danger: true,
              },
            ],
          },
        ]}
      />
    </div>
  )
}

/**
 * `/home` for whoever is entitled to it, and a redirect for whoever is not
 * (D-384).
 *
 * **Caught on production, an hour after the rest of D-384 deployed.** `/home`
 * rendered `HomeScreen` unconditionally, so Tonya — who `landingFor` sends to
 * the timetable — got a composed home if she typed the address, greeting and
 * all. That is one person and two addresses showing different things, which is
 * the exact shape this decision exists to remove; deleting `SignedInHome` took
 * its redirect with it and nothing replaced it.
 *
 * **It asks `landingFor` rather than re-testing the capability**, so there is
 * still one ladder. Anyone whose landing *is* `/home` stays; anyone else is sent
 * to theirs. That makes the invariant literally true — you can only ever be on
 * your own landing screen — instead of true only for people who arrive by
 * navigation.
 */
function HomeAddress() {
  const { session, isLoading } = useSession()

  if (isLoading) return null
  if (!session) return <Navigate to="/signin" replace />

  const landing = landingFor(navSubjectFrom(session))

  return landing === '/home' ? <HomeScreen /> : <Navigate to={landing} replace />
}

/*
 * `SignedInHome` is gone (D-384).
 *
 * **It was the second answer to the question D-370 created `landingFor` to
 * answer once.** That entry says two functions had disagreed and the fix was to
 * centralise — but only `HomeRoute` was emptied. This one kept rung one, the
 * `ViewAllSchedules` redirect, and owned `/home` and `/calendar` with no test
 * of any kind against it. Every change to the ladder had to be made twice, and
 * one copy had a guard.
 *
 * `/home` renders `HomeScreen` directly now, and the one redirect it carried
 * lives on `/calendar` behind the capability its siblings already required.
 * D-176's ladder is unchanged in substance: whoever runs the timetable gets the
 * week grid, everybody else gets the composed home.
 */

/** The signed-in application. Everything here is behind a session. */
function StudioApp() {
  return (
    // A column, so the credit can sit on the bottom edge of a short screen
    // rather than floating under the content (D-223).
    <div className="flex min-h-full flex-col">
      {/*
        Above everything, including the nav, so it is read before any screen is
        and cannot be mistaken for part of one (D-386). It draws nothing at all
        unless a date is being simulated, which is every ordinary host.

        ⚠ This said "so it cannot be scrolled past", which was never true of
        any banner here - they are ordinary blocks at the top of a column, and
        the phone nav is `fixed bottom-0`, so scrolling leaves all of them
        behind. Only the one below is pinned, and only because being missed has
        a cost there (D-902). Corrected rather than fixed: a tester meets the
        date banner on arrival, which is when it has to land.
      */}
      <SimulatedDateBanner />

      {/*
        Above the nav for the same reason the one above it is (D-520): an
        installed app has no address bar and no reload button, so this is the
        only way its owner learns the app has moved on. Draws nothing until the
        build being served stops matching the one running here.

        **Pinned, unlike its neighbours** (D-902): a stale app is most often
        noticed misbehaving deep inside a screen somebody has scrolled, which is
        precisely where a banner at the top of the document is not.
      */}
      <NewVersionBanner />

      {/*
        <b>What the studio is telling this person today</b> (D-790). Above the
        nav for the same reason as the two above it, and it draws nothing when
        there is nothing to say - which is every ordinary day. Unlike what is
        new, this one can be published while a defect is live, because it is
        data rather than a file in the build.
      */}
      <NoticeBanner />

      {/*
        **The one-off hello, in the shell rather than on Home** (D-651).

        Owner: *"After first opening the app, a modal telling first-time users
        they can set up notifications and biometrics in account settings."*
        *Opening the app* is not the same as *reaching Home*: an invitation
        email lands somebody on the screen it was about, and a parent following
        a link to her dancer's record would never have seen it. It renders
        nothing at all unless the server says she has never been told, and the
        server says that is never true of a classroom tablet.
      */}
      <WelcomeDialog />
      {/* What changed since she last opened the app, once the welcome is behind her (D-731). */}
      <WhatsNewDialog />

      <Nav />

      {/*
        The rail beside the content, not above it (D-323).

        **`min-h-0` on the row and `min-w-0` on the column are both
        load-bearing.** A flex child defaults to the size of its content, so
        without them a wide table or an unbreakable class name pushes the
        column wider than the viewport and takes the rail off screen with it
        - the same family of fault as D-254, one axis over.

        The bottom bar is `fixed`, so the column pads for it below 640px and
        stops the last row of every screen sitting under it.
      */}
      <div className="flex min-h-0 flex-1">
        <NavRail />

        <div className="flex min-w-0 flex-1 flex-col pb-16 sm:pb-0">
          {/* The views inside this destination, if it earned a row (Rule 3). */}
          <ViewBar />

          {/*
            Surfaced rather than navigated to (D-324, owner-raised).

            A request awaiting an answer needs a decision from whoever runs the
            timetable, and is not a place. Renders nothing when there is
            nothing, which is why it never becomes furniture - and it costs no
            query for an account that cannot read the list. The clash chip
            that stood beside it went with D-593, once Conflicts had a rail
            destination and a badge of its own; the paragraph below records
            why the strip is above every screen and is history now.

            **It stays above every screen, and an attempt to move it is why
            that is written down** (D-560). `asked`: *"do we need the Conflicts
            tab when we have the orange 17 conflicts this week? seems redundant
            maybe"* — and it is, on that one screen. The obvious fix was to
            show this on Home only. That is wrong here: `landingFor` sends
            anybody with `ViewAllSchedules` to the master calendar, so **the
            owner's Home *is* the calendar** and Home-only would have taken the
            banner away from the one person it is for. Which is what happened,
            and only driving it showed it. The redundancy is answered inside
            the chip instead — see `NeedsADecision`.
          */}
          <div className="page-shell px-5 pt-4 empty:hidden">
            <NeedsADecision />
          </div>

      <Routes>
        {/*
          **`/home`, not `/`** (D-370). The outer tree's `/` is `HomeRoute`,
          which ranks above the `*` that mounts this app — so a `/` route here
          could never match, and the composed home it points at was dead code
          for the accounts D-331 built it for. `HomeRoute` now sends anyone with
          both halves to this address deliberately.
        */}
        <Route path="/home" element={<HomeAddress />} />
        {/*
          Where a tapped push notification lands (D-530). It resolves and
          redirects; nothing renders here. No capability gate, because the row
          is scoped to the account by ownership rather than by permission —
          the same reasoning the bell's endpoints carry (D-324).
        */}
        <Route path="/n/:id" element={<NotificationLink />} />
        {/*
          **`/calendar` is gated like its four siblings now** (D-384).

          It mapped to the same composed home with no `RequireCapability`, while
          `/calendar/week/:date`, `/month`, `/day/:date` and `/teachers/:date`
          all carry one — so it was a silent fifth address for Home, reachable
          by every parent and teacher, and the rail lit *Calendar* for a
          destination they do not have. Measured: a parent-only account opened
          it and got the full composed home, Today panel and all.
        */}
        <Route
          path="/calendar"
          element={
            <RequireCapability capability="ViewAllSchedules">
              {/* The day, matching where `landingFor` sends her (D-599). */}
              <Navigate to={`/calendar/day/${today()}`} replace />
            </RequireCapability>
          }
        />
        <Route
          path="/calendar/week/:date"
          element={
            <RequireCapability capability="ViewAllSchedules">
              <WeekRoute />
            </RequireCapability>
          }
        />
        <Route
          path="/calendar/month"
          element={
            <RequireCapability capability="ViewAllSchedules">
              <MonthRoute />
            </RequireCapability>
          }
        />
        <Route
          path="/calendar/day/:date"
          element={
            <RequireCapability capability="ViewAllSchedules">
              <DayRoute />
            </RequireCapability>
          }
        />
        <Route
          path="/calendar/teachers/:date"
          element={
            <RequireCapability capability="ViewAllSchedules">
              <TeacherDayView />
            </RequireCapability>
          }
        />
        <Route
          path="/find"
          element={
            <RequireCapability capability="ViewPeople">
              <WhereIsView />
            </RequireCapability>
          }
        />
        <Route
          path="/conflicts"
          element={
            <RequireCapability capability="ViewAllSchedules">
              <ConflictsView />
            </RequireCapability>
          }
        />
        <Route
          path="/admin/content"
          element={
            <RequireCapability capability="EditSiteContent">
              <ContentEditorView />
            </RequireCapability>
          }
        />
        <Route
          path="/admin/media"
          element={
            <RequireCapability capability="ManageMedia">
              <MediaLibraryView />
            </RequireCapability>
          }
        />
        {/*
          Behind a session and nothing more (D-320). Every kind of person has an
          account, including Tonya, who has no portal to hang this off.
        */}
        <Route path="/account" element={<AccountView />} />
        {/* Everything new in the app for her, seen or not (D-731). */}
        <Route path="/whats-new" element={<WhatsNewView />} />
        {/*
          **`/me` and `/teacher` are gone** (D-384).

          They were the two single-purpose portals the switch D-331 retired, and
          they outlived it as landing addresses. Both had rotted where nothing
          looked: `/teacher` rendered 103 characters and no heading for a
          teacher with twenty-one classes, and `/me` told her *"Nothing here
          yet"* — a false statement rather than a refusal. `/home` composes both
          halves correctly for every account shape, so these were a second
          implementation that only ever drifted.

          Deleted outright rather than redirected, because nothing is deployed
          to a family yet and there are no bookmarks to keep alive. A redirect
          would be a third answer to "where does she live" surviving the removal
          of the other two.
        */}

        {/*
          Destinations, at their own URLs (D-322, D-326).

          <b>A mode is remembered; a view is linked.</b> Both portals held their
          tab in `useState`, so nothing could link to one - not the rail, not a
          notification row, not a teacher sending Tonya "look at this". Gated by
          `RequireDestination`, which calls the same `reaches()` the rail does,
          because two implementations of "may she go here" drift and the drift
          shows up as a URL that works while nothing offers it.
        */}
        <Route
          path="/schedule"
          element={
            <RequireDestination destination="schedule">
              <DestinationScreen destination="schedule">
                <ScheduleScreen />
              </DestinationScreen>
            </RequireDestination>
          }
        />

        {/*
          The two halves of a dual-role account's week (D-352). The same
          destination, the same guard, the same screen — only the address
          differs, which is what makes these views rather than a mode: nothing
          remembers which she was on, because the URL says so every time.

          An account with one relationship never arrives here. The bar that
          offers them renders only when both relationships hold, and the plain
          `/schedule` redirects only in that case; otherwise it renders her one
          half and keeps its own heading.
        */}
        <Route
          path="/schedule/classes"
          element={
            <RequireDestination destination="schedule">
              <DestinationScreen destination="schedule">
                <ScheduleScreen />
              </DestinationScreen>
            </RequireDestination>
          }
        />
        <Route
          path="/schedule/dancers"
          element={
            <RequireDestination destination="schedule">
              <DestinationScreen destination="schedule">
                <ScheduleScreen />
              </DestinationScreen>
            </RequireDestination>
          }
        />
        <Route
          path="/my-classes"
          element={
            <RequireDestination destination="classes">
              <DestinationScreen destination="classes">
                <MyClasses />
              </DestinationScreen>
            </RequireDestination>
          }
        />
        {/*
          The studio-wide queue, one destination with two views (D-322).

          <b>Both halves addressable</b>, because D-321 folded them into a menu
          and a menu item cannot be linked to - which the bell will need (D-324).
          The teacher keeps /registers and /absences until step 3 re-homes them
          inside her Classes; this one is gated on ManageAttendance, so it is
          Tonya's and does not double up on hers.
        */}
        {/* Standing first, Missing last (D-590). */}
        <Route
          path="/attendance"
          element={
            <RequireDestination destination="attendance">
              <AttendanceScreen view="standing" />
            </RequireDestination>
          }
        />
        <Route
          path="/attendance/absences"
          element={
            <RequireDestination destination="attendance">
              <AttendanceScreen view="absences" />
            </RequireDestination>
          }
        />
        {/*
          Banked classes (D-745). Two doors, one screen, scoped by the server:
          this is the studio's, and /my-banked is a teacher's from her Classes -
          the same split /my-standing already makes, because this destination is
          ManageAttendance and a teacher sent here is told Not your page.
        */}
        <Route
          path="/attendance/banked"
          element={
            <RequireDestination destination="attendance">
              <BankedClassesView />
            </RequireDestination>
          }
        />
        <Route
          path="/attendance/missing"
          element={
            <RequireDestination destination="attendance">
              <AttendanceScreen view="to-mark" />
            </RequireDestination>
          }
        />
        {/*
          <b>One layout route, three unchanged URLs</b> (D-702).

          `asked`: threads on the left, the conversation on the right, the way
          Discord and Teams do it. The mechanism is the nesting - the list is
          rendered by the parent, so it survives the child changing, which is
          exactly what three flat routes could not do. Every link, push
          notification and bookmark still points at the same address.

          The guard and the shell move to the parent and are therefore stated
          once rather than three times, which is also what stops them drifting
          apart the next time one of these gains a sibling.
        */}
        <Route
          path="/messages"
          element={
            <RequireDestination destination="messages">
              <DestinationScreen destination="messages">
                <MessagesLayout />
              </DestinationScreen>
            </RequireDestination>
          }
        >
          {/*
            <b>Absolute child paths, not `new` and `:id`.</b> React Router takes
            either as long as the child starts with the parent, and the relative
            form broke `routes.test.ts` in both directions at once: it reads
            every `path="…"` in this file as an address, so `/messages/new`
            became a link pointing at nothing and `new` became a route nothing
            links to. Writing them out costs nine characters and keeps the one
            check that can catch a dead link working for the next person.
          */}
          <Route index element={<NoConversationPicked />} />
          <Route path="/messages/new" element={<NewConversationView />} />
          <Route path="/messages/:id" element={<ConversationView />} />
        </Route>
        {/*
          The supervisory view (D-626, D-629). The staff half is gated on the
          capability and NOT on the messaging destination: it survives the
          module being switched off, which is the point of its own switch
          (D-625). The guardian half is participation and follows the module
          on the server, where the refusal is a 404 like every other.
        */}
        <Route
          path="/admin/staff/:staffId/conversations"
          element={
            <RequireCapability capability="ReviewConversations">
              <ReviewConversationsView source="staff" />
            </RequireCapability>
          }
        />
        <Route
          path="/admin/staff/:staffId/conversations/:id"
          element={
            <RequireCapability capability="ReviewConversations">
              <SupervisoryTranscript source="staff" />
            </RequireCapability>
          }
        />
        {/*
          The same two screens over a parent's account (D-631), so that every
          conversation in the studio is reachable: staff, parents and dancers
          are the three kinds of person who can hold a seat.
        */}
        <Route
          path="/admin/guardians/:guardianId/conversations"
          element={
            <RequireCapability capability="ReviewConversations">
              <ReviewConversationsView source="guardians" />
            </RequireCapability>
          }
        />
        <Route
          path="/admin/guardians/:guardianId/conversations/:id"
          element={
            <RequireCapability capability="ReviewConversations">
              <SupervisoryTranscript source="guardians" />
            </RequireCapability>
          }
        />
        <Route path="/portal/dancers/:dancerId/conversations" element={<ReviewConversationsView source="dancers" />} />
        <Route path="/portal/dancers/:dancerId/conversations/:id" element={<SupervisoryTranscript source="dancers" />} />
        <Route
          path="/news"
          element={
            <RequireDestination destination="news">
              <DestinationScreen destination="news" measure="narrow">
                <StudioNewsPanel />
              </DestinationScreen>
            </RequireDestination>
          }
        />
        {/*
          Gated on the destination its own way back leads to (D-353). It was
          ungated while `/schedule` is not, so the back link could have met
          Tonya — staff, teaching nothing, nobody's parent — or a teacher whose
          classes had been reassigned, with "Not your page". A screen and its
          exit disagreeing about who may be there is the same fault as a link
          that refuses the person holding it.
        */}
        <Route
          path="/asks"
          element={
            <RequireCapability capability="ViewSchedule">
              <AsksView />
            </RequireCapability>
          }
        />
        <Route
          path="/my-attendance"
          element={
            <RequireCapability capability="ViewAttendance">
              <MissingAttendanceView />
            </RequireCapability>
          }
        />
        {/* A teacher's own Standing (D-590): the server scopes it to her classes. */}
        <Route
          path="/my-banked"
          element={
            <RequireCapability capability="ViewAttendance">
              <BankedClassesView />
            </RequireCapability>
          }
        />
        <Route
          path="/my-standing"
          element={
            <RequireCapability capability="ViewAttendance">
              <StandingView />
            </RequireCapability>
          }
        />
        {/*
          Scoped by the server, not by the route (D-83, D-188): a teacher gets
          her own classes, Tonya the studio. One screen, one permission model.
        */}
        <Route
          path="/absences"
          element={
            <RequireCapability capability="ViewAttendance">
              <ReportedAbsencesView />
            </RequireCapability>
          }
        />
        {/*
          Both folded into /admin/classes (D-205). Redirected rather than
          removed: these were in the menu for weeks and are in somebody's
          bookmarks, and a 404 on a screen that moved reads as a screen that
          broke.
        */}
        <Route path="/admin/class-types" element={<Navigate to="/admin/classes" replace />} />
        <Route
          path="/admin/attendance-groups"
          element={<Navigate to="/admin/classes" replace />}
        />
        <Route
          path="/admin/people"
          element={
            /*
             * <b>The nav's own answer, not a second one</b> (D-800, found by a
             * sweep).
             *
             * This asked for `ViewPeople`, which <b>every teacher holds</b> so
             * she can read a roster — while `nav.ts` deliberately withholds the
             * destination behind `ManagePeople` or `ManageStaff`, with the
             * reason written beside it: <i>"that is not the same as needing a
             * people directory on her rail."</i> So the rail offered nothing
             * and the URL worked, and any teacher who typed it read all 165
             * families' home addresses.
             *
             * That is the exact failure `RequireDestination` exists for, and
             * which CLAUDE.md names outright: <i>a URL that works while nothing
             * offers it is the worse half, because nobody is looking for it.</i>
             * One function answers it now, so the two cannot drift again.
             */
            <RequireDestination destination="people">
              <PeopleAdminView />
            </RequireDestination>
          }
        />
        <Route
          path="/admin/dancers"
          element={
            <RequireCapability capability="ViewPeople">
              <DancerAdminView />
            </RequireCapability>
          }
        />
        <Route
          path="/admin/dancers/:id"
          element={
            <RequireCapability capability="ViewPeople">
              <DancerRecordView />
            </RequireCapability>
          }
        />
        <Route
          path="/admin/messages"
          element={
            <RequireCapability capability="ManageNotificationTemplates">
              <NotificationTemplatesView />
            </RequireCapability>
          }
        />
        <Route
          /*
            Rooms and seasons, gated on the capabilities that are about rooms
            and seasons.

            It required `ManageClasses`, which is neither - so an account
            granted `ManageRooms` alone (D-5) got a Studio row on the rail that
            opened "Not your page". The rail decides who may go where (D-322);
            a route gate reading a different capability is the second
            implementation D-261 warns about, and this is what it looks like.
          */
          path="/admin/studio"
          element={
            <RequireDestination destination="studio">
              <StudioSetupView />
            </RequireDestination>
          }
        />
        <Route
          path="/admin/classes"
          element={
            <RequireCapability capability="ManageClasses">
              <ClassesView />
            </RequireCapability>
          }
        />
        {/*
          One class, on its own page (D-566). The same capability as the list,
          so a link out of a search or a conflict cannot land somebody on a
          screen that refuses them — one function answers "may she go here",
          and it is this route.
        */}
        <Route
          path="/admin/classes/:classId"
          element={
            <RequireCapability capability="ManageClasses">
              <ClassRecordRoute />
            </RequireCapability>
          }
        />
        {/*
          Measuring a whole class (D-440). Not under /admin, because a teacher
          reaches it for her own classes and she holds no admin capability -
          gating it on RecordMeasurements is what lets one screen serve both her
          and Tonya, which is the point of shipping the slices together.
        */}
        <Route
          path="/classes/:classId/measure"
          element={
            <RequireCapability capability="RecordMeasurements">
              <MeasuringView />
            </RequireCapability>
          }
        />
        {/*
          Telling people about a fault without a deploy (D-790). Beside
          Feedback deliberately: one is what they told us, the other is what we
          are telling them, and both are studio-level rather than about a class
          or a person.
        */}
        <Route
          path="/admin/notices"
          element={
            <RequireCapability capability="SendAnnouncements">
              <DestinationScreen destination="notices">
                <NoticesView />
              </DestinationScreen>
            </RequireCapability>
          }
        />
        {/*
          What needs her today (D-877). `ViewAllSchedules` is the "runs the
          studio" marker the endpoint itself asks for; each row is gated again
          on the server by what its own data needs, so an admin reaching this
          screen simply gets fewer rows than Tonya rather than a different one.
        */}
        <Route
          path="/dashboard"
          element={
            <RequireCapability capability="ViewAllSchedules">
              <DashboardView />
            </RequireCapability>
          }
        />
        {/* What the pilot has told us (D-607). */}
        <Route
          path="/admin/feedback"
          element={
            <RequireCapability capability="ViewFeedback">
              <DestinationScreen destination="feedback">
                <FeedbackView />
              </DestinationScreen>
            </RequireCapability>
          }
        />
        <Route
          path="/admin/cover"
          element={
            <RequireCapability capability="ManageClasses">
              <DestinationScreen destination="class-admin">
                <AdminCoverView />
              </DestinationScreen>
            </RequireCapability>
          }
        />
        <Route
          path="/admin/devices"
          element={
            <RequireCapability capability="ManageStaff">
              <DeviceAdminView />
            </RequireCapability>
          }
        />
        <Route
          path="/admin/staff"
          element={
            <RequireCapability capability="ManageStaff">
              <StaffAdminView />
            </RequireCapability>
          }
        />
        <Route
          path="/admin/import"
          element={
            <RequireCapability capability="ManageIntegrations">
              <ImportView />
            </RequireCapability>
          }
        />
        {/* What the feed has received, and it changes nothing (D-816). */}
        <Route
          path="/admin/import/activity"
          element={
            <RequireCapability capability="ManageIntegrations">
              <FeedActivityView />
            </RequireCapability>
          }
        />
        {/* The other half of the import: what has to go back by hand (D-694). */}
        <Route
          path="/admin/carryover"
          element={
            <RequireCapability capability="ManageCarryover">
              <CarryoverView />
            </RequireCapability>
          }
        />
        <Route
          path="*"
          element={
            <div className="mx-auto w-full max-w-[600px] px-5 py-16 text-center">
              <h1 className="font-display text-2xl">Page not found</h1>
              <p className="mt-2 text-sm text-ink-3">
                That address doesn&rsquo;t match anything in the studio system.
              </p>
            </div>
          }
        />
      </Routes>

          <BuildCredit tone="quiet" />
        </div>
      </div>
    </div>
  )
}


export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Reachable without a session. Accept-invite has to be among them —
            the whole point is that the account has no password yet — and
            /classes is the public catalog, the app's only anonymous data
            surface (D-28). Everything else falls through to RequireSession. */}
        <Route path="/signin" element={<SignInView />} />
        <Route path="/accept-invite" element={<AcceptInviteView />} />
        {/* Public by necessity: an existing family has no account yet (D-155). */}
        <Route path="/claim-account" element={<ClaimAccountView />} />
        <Route path="/forgot-password" element={<ForgotPasswordView />} />
        <Route path="/reset-password" element={<ResetPasswordView />} />
        <Route path="/" element={<FrontDoor />} />

        {/*
          The homepage as a visitor sees it, whoever is asking.
          `/` cannot do this job: signed in, HomeRoute sends you to the
          calendar, the teacher portal or your dancer (D-128) — so the content
          editor's "View the site" link opened another copy of the app, which
          is the one thing it must never show. Above RequireSession
          deliberately, so previewing never depends on being signed out.
        */}
        <Route path="/site" element={<PublicShell><HomeView /></PublicShell>} />

        <Route path="/classes" element={<PublicShell><CatalogView /></PublicShell>} />
        <Route path="/about" element={<PublicShell><AboutView /></PublicShell>} />

        {/*
          The studio tablet (D-116). No PublicShell and no RequireSession: it is
          a device, not a visitor and not an account. Authorisation is the device
          token plus a PIN, and the screen manages its own chrome because it is
          bolted to a wall and shows one thing at a time.
        */}
        <Route path="/room" element={<ClassroomView />} />

        {/*
          `/register`, `/tryouts` and `/admin/tryouts` are **parked, not
          deleted** (D-111). Registration and payment run through Jackrabbit
          while it still owns billing, so routing to our own wizard would create
          enrolments Jackrabbit never learns to bill. The views, services,
          endpoints and tests all remain and are exercised by the suite — the
          only thing removed is the way in, which is what makes switching them
          back on a three-line change rather than a rebuild.
        */}

        <Route
          path="*"
          element={
            <RequireSession>
              <StudioApp />
            </RequireSession>
          }
        />
      </Routes>
    </BrowserRouter>
  )
}
